import React, { useState } from 'react';
import { translations } from '@/context/translations';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableSection } from '../SortableSection';
import { EditableText } from '../EditableText';
import styles from '../cv-builder.module.css';

export interface TemplateProps {
  cvData: any;
  setCvData: React.Dispatch<React.SetStateAction<any>>;
  fontScale: number;
  avatarUrl: string | null;
  themeColor?: string;
}

export default function Template1({ cvData, setCvData, fontScale, avatarUrl, themeColor = '#4574ba' }: TemplateProps) {
  const isSq = typeof window !== 'undefined' && window.location.pathname.startsWith('/sq');
  const t = (key: string) => isSq ? ((translations.sq as any)?.[key] || key) : ((translations.en as any)?.[key] || key);
  const leftColRef = React.useRef<HTMLDivElement>(null);
  const rightColRef = React.useRef<HTMLDivElement>(null);
  const [targetHeight, setTargetHeight] = useState<number>(1122);

  const [leftSections, setLeftSections] = useState([
    { id: 'avatar' },
    { id: 'nameBlock' },
    { id: 'aboutMe' },
    { id: 'contact' },
    { id: 'languages' }
  ]);
  const [editingSkillIndex, setEditingSkillIndex] = useState<number | null>(null);

  const [rightSections, setRightSections] = useState([
    { id: 'experience' },
    { id: 'trainings' },
    { id: 'education' },
    { id: 'expertise' }
  ]);

  const measureContentBottom = (container: HTMLElement | null, rootTop: number) => {
    if (!container) return 0;
    let maxBottom = 0;
    const items = container.querySelectorAll<HTMLElement>(
      'p, h1, h2, h3, h4, span, img, .page-break-item, [class*="Item"], [class*="item"], [class*="Text"], [class*="text"], [class*="dob"], [class*="nameBlock"], [class*="avatar"]'
    );
    items.forEach((el) => {
      if (el.offsetParent === null) return;
      if (
        el.classList.contains('add-btn') ||
        el.closest('.add-btn') ||
        el.classList.contains('drag-handle') ||
        el.closest('.drag-handle') ||
        el.hasAttribute('data-html2canvas-ignore') ||
        el.closest('[data-html2canvas-ignore]')
      ) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const bottom = rect.bottom - rootTop;
      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    });
    return maxBottom;
  };

  React.useLayoutEffect(() => {
    const container = rightColRef.current;
    const canvasRoot = document.getElementById('cv-pdf-content');
    if (!container || !canvasRoot) return;

    // Reset all pushed margins first
    const pushed = container.querySelectorAll<HTMLElement>('[data-pushed]');
    pushed.forEach((el) => {
      el.style.marginTop = '';
      el.removeAttribute('data-pushed');
    });

    const PAGE_HEIGHT = 1122;
    const SAFE_BOTTOM = 85; // Leave 85px bottom margin for logo and spacing
    const TOP_PADDING = 50;  // 50px from page top (30px clear distance below the 40px separator bar)

    const rootRect = canvasRoot.getBoundingClientRect();
    const candidates = container.querySelectorAll<HTMLElement>('.page-break-section, .page-break-item');

    candidates.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const relativeTop = rect.top - rootRect.top;
      const elHeight = rect.height;

      const pageIndex = Math.floor(relativeTop / PAGE_HEIGHT);
      const pageTopZone = pageIndex * PAGE_HEIGHT + TOP_PADDING;
      const pageBottomThreshold = pageIndex * PAGE_HEIGHT + (PAGE_HEIGHT - SAFE_BOTTOM);

      if (pageIndex > 0 && relativeTop < pageTopZone) {
        // Element started too close to top edge of Page 2+
        const push = pageTopZone - relativeTop;
        if (push > 0) {
          el.style.marginTop = `${push}px`;
          el.setAttribute('data-pushed', 'true');
        }
      } else if (relativeTop + Math.min(elHeight, 60) > pageBottomThreshold) {
        // Element crosses into bottom margin of current page -> jump to next page
        const targetTop = (pageIndex + 1) * PAGE_HEIGHT + TOP_PADDING;
        const push = targetTop - relativeTop;
        if (push > 0) {
          el.style.marginTop = `${push}px`;
          el.setAttribute('data-pushed', 'true');
        }
      }
    });

    // Measure actual content bottom
    const leftBottom = measureContentBottom(leftColRef.current, rootRect.top);
    const rightBottom = measureContentBottom(rightColRef.current, rootRect.top);
    const maxContentH = Math.max(leftBottom, rightBottom);

    // Only create page 2 if real content actually extends onto page 2
    let pages = 1;
    if (maxContentH > PAGE_HEIGHT + 35) {
      pages = Math.min(3, Math.ceil(maxContentH / PAGE_HEIGHT));
    }
    setTargetHeight(pages * PAGE_HEIGHT);
  }, [cvData, fontScale, leftSections, rightSections]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      // Check left
      const leftActive = leftSections.find(s => s.id === active.id);
      if (leftActive) {
        setLeftSections((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          return arrayMove(items, oldIndex, newIndex);
        });
        return;
      }

      // Check right
      const rightActive = rightSections.find(s => s.id === active.id);
      if (rightActive) {
        setRightSections((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
    }
  };

  const updateArrayField = (field: string, index: number, key: string, val: string) => {
    const newArray = [...(cvData[field] || [])];
    if (newArray[index]) {
      newArray[index] = { ...newArray[index], [key]: val };
      setCvData({ ...cvData, [field]: newArray });
    }
  };

  const addArrayItem = (field: string, defaultItem: any) => {
    const newArray = [...(cvData[field] || []), defaultItem];
    setCvData({ ...cvData, [field]: newArray });
  };

  const removeArrayItem = (field: string, index: number) => {
    const newArray = [...(cvData[field] || [])];
    newArray.splice(index, 1);
    setCvData({ ...cvData, [field]: newArray });
  };

  const renderLeftSection = (id: string) => {
    switch (id) {
      case 'avatar':
        const displayAvatar = cvData.customAvatar || avatarUrl;
        return (
          <div style={{ width: '100%' }}>
            <label style={{ display: 'block', position: 'relative', cursor: 'pointer', margin: '0 auto', width: '100%' }} className="avatar-upload-container">
              <style dangerouslySetInnerHTML={{ __html: `
                .avatar-upload-overlay { opacity: 0; transition: opacity 0.2s; position: absolute; inset: 0; background: rgba(0,0,0,0.5); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; pointer-events: none; }
                .avatar-upload-container:hover .avatar-upload-overlay { opacity: 1; }
              ` }} />
              <input
                type="file"
                accept=".jpg, .jpeg, .png, .webp, .heic, .heif, image/jpeg, image/png, image/webp, image/heic, image/heif"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setCvData({ ...cvData, customAvatar: reader.result as string });
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              {displayAvatar ? (
                <img src={displayAvatar} alt="Avatar" className={styles.avatar} />
              ) : (
                <div className={styles.avatar} style={{ backgroundColor: '#44403c' }} />
              )}
              <div className="avatar-upload-overlay" data-html2canvas-ignore>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
              </div>
            </label>
          </div>
        );
      case 'nameBlock':
        return (
          <div className={styles.nameBlock}>
            <EditableText
              value={cvData.name || ''}
              onChange={(val) => setCvData({ ...cvData, name: val })}
              className={styles.nameBlock}
              style={{ padding: 0, margin: 0, fontWeight: 800, fontSize: (cvData.name || '').length > 10 ? `${2.5 * (10 / (cvData.name || '').length)}em` : '2.5em', textTransform: 'uppercase', lineHeight: 1.1, letterSpacing: '2px', display: 'inline-block', width: '100%', wordBreak: 'break-word' }}
            />
            <EditableText
              value={cvData.surname || ''}
              onChange={(val) => setCvData({ ...cvData, surname: val })}
              style={{ fontWeight: 300, fontSize: (cvData.surname || '').length > 10 ? `${2.5 * (10 / (cvData.surname || '').length)}em` : '2.5em', textTransform: 'uppercase', lineHeight: 1.1, letterSpacing: '2px', display: 'inline-block', width: '100%', wordBreak: 'break-word' }}
            />
            <EditableText
              value={(() => {
                if (!cvData.birthDate) return '';
                const parts = String(cvData.birthDate).split(/[-/.]/);
                if (parts.length === 3) {
                  if (parts[0].length === 4) {
                    return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
                  }
                  return `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[2]}`;
                }
                const d = new Date(cvData.birthDate);
                if (isNaN(d.getTime())) return String(cvData.birthDate);
                const day = String(d.getUTCDate()).padStart(2, '0');
                const month = String(d.getUTCMonth() + 1).padStart(2, '0');
                const year = d.getUTCFullYear();
                return `${day}/${month}/${year}`;
              })()}
              onChange={(val) => setCvData({ ...cvData, birthDate: val })}
              className={styles.dob}
              style={{ display: 'inline-block', width: '100%' }}
            />
          </div>
        );
      case 'aboutMe':
        if (cvData.aboutMe === undefined) return null;
        return (
          <div style={{ position: 'relative' }}>
            <h2 className={styles.aboutMeHeader}>About Me</h2>
            <div className={styles.aboutMeText}>
              <EditableText
                value={cvData.aboutMe || ''}
                onChange={(val) => setCvData({ ...cvData, aboutMe: val })}
                multiline
                maxWords={65}
              />
            </div>
          </div>
        );
      case 'contact':
        return (
          <div className={styles.leftSection}>
            <h2 className={styles.leftHeader}>Contact</h2>

            <div className={styles.contactListWrapper}>
              <div className={styles.contactItem}>
                <div className={styles.contactIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                </div>
                <div className={styles.contactText}>
                  <span style={{ fontWeight: 600 }}>Phone</span>
                  <EditableText value={cvData.phone || ''} onChange={(val) => setCvData({ ...cvData, phone: val })} />
                </div>
              </div>

              <div className={styles.contactItem}>
                <div className={styles.contactIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><path d="M22 6L12 13L2 6"></path></svg>
                </div>
                <div className={styles.contactText}>
                  <span style={{ fontWeight: 600 }}>Email</span>
                  <EditableText value={cvData.email || ''} onChange={(val) => setCvData({ ...cvData, email: val })} />
                </div>
              </div>

              <div className={styles.contactItem}>
                <div className={styles.contactIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                </div>
                <div className={styles.contactText}>
                  <span style={{ fontWeight: 600 }}>Address</span>
                  <EditableText
                    value={[cvData.address, cvData.city, cvData.country].filter(Boolean).join(', ') || ''}
                    onChange={(val) => {
                      const parts = val.split(',').map(s => s.trim());
                      setCvData({ ...cvData, address: parts[0] || '', city: parts[1] || '', country: parts[2] || '' });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      case 'languages':
        const langs = cvData.languages || [];
        return (
          <div className={styles.leftSection}>
            <h2 className={styles.leftHeader} style={{ display: 'flex', alignItems: 'center' }}>
              Languages
            </h2>
            {langs.map((l: any, i: number) => (
              <div key={i} style={{ position: 'relative', marginBottom: '0.75rem' }}>
                <div className={styles.languageItem} style={{ margin: 0 }}>
                  <div className={styles.languageSquare}></div>
                  <EditableText
                    value={`${l.language || 'Language'} ${l.level ? `- ${l.level}` : ''}`}
                    onChange={(val) => {
                      const parts = val.split('-');
                      updateArrayField('languages', i, 'language', parts[0]?.trim() || '');
                      if (parts[1]) updateArrayField('languages', i, 'level', parts[1].trim());
                    }}
                  />
                </div>
                <button
                  data-html2canvas-ignore
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => removeArrayItem('languages', i)}
                  style={{ position: 'absolute', top: 0, right: '-8px', background: '#fee2e2', border: '1px solid #f87171', color: '#dc2626', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
                  title="Remove Language"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: langs.length > 0 ? '1rem' : '0' }}>
              <button
                className="add-btn"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => addArrayItem('languages', { language: 'New Language', level: 'Level' })}
                style={{ background: 'none', border: '1px dashed #57534e', color: '#a8a29e', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem' }}
              >
                {t('+ Add Language')}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const toggleVisibility = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const hidden = cvData.hiddenSections || [];
    if (hidden.includes(id)) {
      setCvData({ ...cvData, hiddenSections: hidden.filter((h: string) => h !== id) });
    } else {
      setCvData({ ...cvData, hiddenSections: [...hidden, id] });
    }
  };

  const renderVisibilityToggle = (id: string, isHidden: boolean) => (
    <button
      data-html2canvas-ignore
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => toggleVisibility(id, e)}
      style={{ marginLeft: '8px', background: 'none', border: 'none', color: isHidden ? '#ef4444' : '#a8a29e', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      title={isHidden ? "Show Section" : "Hide Section"}
    >
      {isHidden ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
      )}
    </button>
  );

  const renderRightSection = (id: string, isHidden: boolean) => {
    switch (id) {
      case 'expertise':
        const skillsList = cvData.skills || [];
        return (
          <div>
            <div className={styles.rightSectionHeader} style={{ display: 'flex', alignItems: 'center' }}>
              <div className={styles.rightIconCircle} style={{ backgroundColor: themeColor }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path><path d="M22 12A10 10 0 0 0 12 2v10z"></path></svg>
              </div>
              <h2 className={styles.rightTitle}>Skills</h2>
            </div>
            <div className={styles.skillsGrid}>
              <style dangerouslySetInnerHTML={{ __html: `
                .skill-row-container .skill-actions { opacity: 0; transition: opacity 0.2s; pointer-events: none; }
                .skill-row-container:hover .skill-actions { opacity: 1; pointer-events: auto; }
              ` }} />
              {skillsList.map((s: any, i: number) => (
                <div key={i} className="skill-row-container" style={{ position: 'relative', padding: '12px 45px 12px 0', margin: '-12px -45px -12px 0' }}>
                  <div className={styles.skillItem}>
                    <EditableText
                      value={typeof s === 'string' ? s : (s.name || 'New Skill')}
                      onChange={(val) => {
                        const newSkills = [...cvData.skills];
                        const currentLevel = typeof s === 'string' ? 80 : (s.level || 80);
                        newSkills[i] = { name: val, level: currentLevel };
                        setCvData({ ...cvData, skills: newSkills });
                      }}
                      className={styles.skillName}
                    />
                    <div style={{ position: 'relative' }}>
                      <div className={styles.skillBarContainer}>
                        <div className={styles.skillBarFill} style={{ width: `${typeof s === 'string' ? 80 : (s.level || 80)}%`, backgroundColor: themeColor, borderRadius: '3px', transition: 'width 0.2s ease' }}></div>
                      </div>
                      {editingSkillIndex === i && (
                        <div
                          data-html2canvas-ignore
                          onPointerDown={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            top: '-32px',
                            right: '0px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            zIndex: 20,
                            cursor: 'default'
                          }}
                        >
                          <input
                            type="number"
                            min="0" max="100"
                            value={typeof s === 'string' ? 80 : (s.level || 80)}
                            onChange={(e) => {
                              const newSkills = [...cvData.skills];
                              const currentName = typeof s === 'string' ? s : (s.name || 'New Skill');
                              newSkills[i] = { name: currentName, level: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) };
                              setCvData({ ...cvData, skills: newSkills });
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            style={{
                              width: '60px',
                              padding: '2px 4px',
                              fontSize: '0.9em',
                              border: '1px solid #9ca3af',
                              borderRadius: '4px',
                              boxSizing: 'border-box',
                              textAlign: 'center',
                              backgroundColor: 'white'
                            }}
                            placeholder="%"
                            title="Skill Level (0-100)"
                          />
                          <button
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => setEditingSkillIndex(null)}
                            style={{
                              fontSize: '0.85em',
                              padding: '4px 10px',
                              backgroundColor: '#2563eb',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Done
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="skill-actions" style={{ position: 'absolute', top: '10px', right: '-10px', display: 'flex', gap: '4px', zIndex: 10 }}>
                    <button
                      data-html2canvas-ignore
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => setEditingSkillIndex(editingSkillIndex === i ? null : i)}
                      style={{ background: editingSkillIndex === i ? '#dbeafe' : '#f3f4f6', border: editingSkillIndex === i ? '1px solid #93c5fd' : '1px solid #d1d5db', color: editingSkillIndex === i ? '#2563eb' : '#4b5563', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title={editingSkillIndex === i ? "Done editing" : "Edit skill level"}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    </button>
                    <button
                      data-html2canvas-ignore
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => removeArrayItem('skills', i)}
                      style={{ background: '#fee2e2', border: '1px solid #f87171', color: '#dc2626', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title="Remove Skill"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: skillsList.length > 0 ? '1.5rem' : '0' }}>
              <button
                className="add-btn"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => addArrayItem('skills', { name: 'New Skill', level: 80 })}
                style={{ background: 'none', border: '1px dashed #d6d3d1', color: '#a8a29e', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                {t('+ Add Skill')}
              </button>
            </div>
          </div>
        );
      case 'experience':
        const expList = cvData.workExperience || [];
        return (
          <div>
            <div className={styles.rightSectionHeader}>
              <div className={styles.rightIconCircle} style={{ backgroundColor: themeColor }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>
              </div>
              <h2 className={styles.rightTitle}>Experience</h2>
            </div>

            <div style={{ marginTop: '2rem' }}>
              {expList.map((exp: any, i: number) => (
                <div key={i} className="page-break-item" style={{ position: 'relative', marginBottom: '1rem' }}>
                  <div className={styles.experienceItem} style={{ margin: 0 }}>
                    <div className={styles.experienceLeft}>
                      <EditableText value={exp.title || ''} onChange={(val) => updateArrayField('workExperience', i, 'title', val)} className={styles.experienceTitle} />
                      <EditableText value={exp.dates || ''} onChange={(val) => updateArrayField('workExperience', i, 'dates', val)} className={styles.experienceDates} />
                      <EditableText value={exp.location || 'Location'} onChange={(val) => updateArrayField('workExperience', i, 'location', val)} className={styles.experienceLocation} />
                    </div>
                    <div className={styles.experienceRight}>
                      <EditableText value={exp.company || ''} onChange={(val) => updateArrayField('workExperience', i, 'company', val)} className={styles.experienceCompany} />
                      <EditableText value={exp.description || ''} onChange={(val) => updateArrayField('workExperience', i, 'description', val)} className={styles.experienceDesc} multiline />
                    </div>
                  </div>
                  <button
                    data-html2canvas-ignore
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => removeArrayItem('workExperience', i)}
                    style={{ position: 'absolute', top: 0, right: '-8px', background: '#fee2e2', border: '1px solid #f87171', color: '#dc2626', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
                    title="Remove Experience"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  </button>
                </div>
              ))}

            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: expList.length > 0 ? '1rem' : '0' }}>
              <button
                className="add-btn"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => addArrayItem('workExperience', { title: 'New Job Title', dates: 'Year - Year', location: 'Location', company: 'Company Name', description: 'Job description...' })}
                style={{ background: 'none', border: '1px dashed #d6d3d1', color: '#a8a29e', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                {t('+ Add Experience')}
              </button>
            </div>
          </div>
        );
      case 'trainings':
        const trList = cvData.trainings || [];
        return (
          <div>
            <div className={styles.rightSectionHeader}>
              <div className={styles.rightIconCircle} style={{ backgroundColor: themeColor }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 10v6M2 10l10-5 10 5-10 5z"></path><path d="M6 12v5c3 3 9 3 12 0v-5"></path></svg>
              </div>
              <h2 className={styles.rightTitle}>Trainings</h2>
            </div>

            <div style={{ marginTop: '2rem' }}>
              {trList.map((t: any, i: number) => (
                <div key={i} className="page-break-item" style={{ position: 'relative', marginBottom: '1rem' }}>
                  <div className={styles.experienceItem} style={{ margin: 0 }}>
                    <div className={styles.experienceLeft}>
                      <EditableText value={t.title || ''} onChange={(val) => updateArrayField('trainings', i, 'title', val)} className={styles.experienceTitle} />
                      <EditableText value={t.dates || ''} onChange={(val) => updateArrayField('trainings', i, 'dates', val)} className={styles.experienceDates} />
                    </div>
                    <div className={styles.experienceRight}>
                      <EditableText value={t.organization || 'Organization'} onChange={(val) => updateArrayField('trainings', i, 'organization', val)} className={styles.experienceCompany} />
                      <EditableText value={t.description || ''} onChange={(val) => updateArrayField('trainings', i, 'description', val)} className={styles.experienceDesc} multiline />
                    </div>
                  </div>
                  <button
                    data-html2canvas-ignore
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => removeArrayItem('trainings', i)}
                    style={{ position: 'absolute', top: 0, right: '-8px', background: '#fee2e2', border: '1px solid #f87171', color: '#dc2626', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
                    title="Remove Training"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  </button>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: trList.length > 0 ? '1rem' : '0' }}>
                <button
                  className="add-btn"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => addArrayItem('trainings', { title: 'New Training', dates: 'Year - Year', organization: 'Organization', description: 'Training description...' })}
                  style={{ background: 'none', border: '1px dashed #d6d3d1', color: '#a8a29e', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '' }}
                >
                  {t('+ Add Training')}
                </button>
              </div>
            </div>
          </div>
        );
      case 'education':
        const eduList = cvData.education || [];
        return (
          <div>
            <div className={styles.rightSectionHeader}>
              <div className={styles.rightIconCircle} style={{ backgroundColor: themeColor }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
              </div>
              <h2 className={styles.rightTitle}>Education</h2>
            </div>

            <div style={{ marginTop: '2rem' }}>
              {eduList.map((e: any, i: number) => (
                <div key={i} className="page-break-item" style={{ position: 'relative', marginBottom: '1rem' }}>
                  <div className={styles.experienceItem} style={{ margin: 0 }}>
                    <div className={styles.experienceLeft}>
                      <EditableText value={e.school || ''} onChange={(val) => updateArrayField('education', i, 'school', val)} className={styles.experienceTitle} />
                      <EditableText value={e.dates || ''} onChange={(val) => updateArrayField('education', i, 'dates', val)} className={styles.experienceDates} />
                    </div>
                    <div className={styles.experienceRight}>
                      <EditableText value={e.degree || ''} onChange={(val) => updateArrayField('education', i, 'degree', val)} className={styles.experienceCompany} />
                      <EditableText value={e.description || ''} onChange={(val) => updateArrayField('education', i, 'description', val)} className={styles.experienceDesc} multiline />
                    </div>
                  </div>
                  <button
                    data-html2canvas-ignore
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => removeArrayItem('education', i)}
                    style={{ position: 'absolute', top: 0, right: '-8px', background: '#fee2e2', border: '1px solid #f87171', color: '#dc2626', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
                    title="Remove Education"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  </button>
                </div>
              ))}

            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: eduList.length > 0 ? '1rem' : '0' }}>
              <button
                className="add-btn"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => addArrayItem('education', { school: 'University/School', dates: 'Year - Year', degree: 'Degree Name', description: 'Description...' })}
                style={{ background: 'none', border: '1px dashed #d6d3d1', color: '#a8a29e', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                {t('+ Add Education')}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div id="cv-pdf-content" className={styles.cvCanvas} style={{ position: 'relative', fontSize: `${11.2 * fontScale}px`, minHeight: `${targetHeight}px`, height: `${targetHeight}px` }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {/* LEFT COLUMN */}
        <div ref={leftColRef} className={styles.leftColumn}>
          <SortableContext items={leftSections} strategy={verticalListSortingStrategy}>
            {leftSections.map((section) => {
              const content = renderLeftSection(section.id);
              if (!content) return null;
              const isBlueSection = ['avatar', 'nameBlock', 'aboutMe'].includes(section.id);
              return (
                <SortableSection key={section.id} id={section.id} disabled={editingSkillIndex !== null}>
                  <div style={{ position: 'relative', backgroundColor: isBlueSection ? themeColor : 'transparent', color: isBlueSection ? 'white' : 'inherit' }}>
                    <div style={{ position: 'absolute', right: '5px', top: '5px', opacity: 0, transition: 'opacity 0.2s', cursor: 'grab', color: isBlueSection ? 'white' : '#1c1917', zIndex: 10 }} className="drag-handle">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" /></svg>
                    </div>
                    <style dangerouslySetInnerHTML={{ __html: `.drag-handle:hover { opacity: 1 !important; } div:hover > .drag-handle { opacity: 0.5; }` }} />
                    {content}
                  </div>
                </SortableSection>
              );
            })}
          </SortableContext>
        </div>

        {/* RIGHT COLUMN */}
        <div ref={rightColRef} className={styles.rightColumn}>
          <SortableContext items={rightSections} strategy={verticalListSortingStrategy}>
            {rightSections.map((section) => {
              const isHidden = (cvData.hiddenSections || []).includes(section.id);
              const content = renderRightSection(section.id, isHidden);
              if (!content) return null;
              return (
                <SortableSection key={section.id} id={section.id} disabled={editingSkillIndex !== null}>
                  <div className="page-break-section" style={{ position: 'relative', border: '2px dashed transparent', borderRadius: '8px', padding: '1rem', margin: '-1rem', opacity: isHidden ? 0.4 : 1 }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                    data-html2canvas-ignore={isHidden ? "true" : undefined}
                  >
                    <div data-html2canvas-ignore style={{ position: 'absolute', right: '12px', top: '12px', display: 'flex', alignItems: 'center', gap: '4px', zIndex: 10 }}>
                      {renderVisibilityToggle(section.id, isHidden)}
                      <div style={{ opacity: 0.3, cursor: 'grab', display: 'flex' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" /></svg>
                      </div>
                    </div>
                    {content}
                  </div>
                </SortableSection>
              );
            })}
          </SortableContext>
        </div>
      </DndContext>

      {/* Page Break Separators - Rendered ONLY if more than 1 page exists */}
      {targetHeight > 1122 && (
        <div data-html2canvas-ignore style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 100, overflow: 'hidden' }}>
          {[...Array(Math.floor(targetHeight / 1122) - 1)].map((_, i) => {
            const pageNum = i + 1;
            return (
              <div
                key={pageNum}
                style={{
                  position: 'absolute',
                  top: `${pageNum * 1122 - 20}px`,
                  left: '-32px',
                  right: '-32px',
                  height: '40px',
                  backgroundColor: '#f3f4f6',
                  boxShadow: '0 6px 10px -3px rgba(0, 0, 0, 0.1), 0 -6px 10px -3px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderTop: '2px solid #e5e7eb',
                  borderBottom: '2px solid #e5e7eb'
                }}
              >
                <span
                  style={{
                    background: '#ffffff',
                    color: '#4b5563',
                    padding: '4px 18px',
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.6px',
                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #d1d5db',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#2563eb' }}></span>
                  PAGE {pageNum + 1}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Repeating Bottom Logo for each A4 Page */}
      <div style={{ position: 'absolute', top: 0, left: '220px', right: 0, bottom: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {[...Array(Math.max(1, Math.floor(targetHeight / 1122)))].map((_, i) => (
          <div key={i} style={{ position: 'absolute', top: `${(i + 1) * 1122 - 35}px`, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
            <img src="/logo.png" alt="Logo" style={{ height: '20px', opacity: 0.15, filter: 'grayscale(100%)' }} />
          </div>
        ))}
      </div>
    </div>
  );
}
